/* ============================================================
   window.PNQuotes — the Discogs buyer feedback line, on a timer.

   One behaviour for both product-page modes: the quote changes on its own at
   a slow walk, and while the pointer is over it the cycle speeds up so you
   can read through every piece of feedback. Defined by whichever of
   pn-sticky.js / pn-afterhours.js parses first.

   The copy is read from the box's own `data-quotes` attribute, so whatever
   fills that attribute (hard-coded Liquid today, a Discogs sync later)
   drives both modes without touching this.
   ============================================================ */
if (!window.PNQuotes) window.PNQuotes = (function () {
  'use strict';
  var SLOW = 1000, FAST = 320, FADE = 110;   /* a change a second; a blur on hover */
  /* Phones only (html.pn-phone, stamped in the head - both orientations):
     200ms. FADE must outlast the CSS `transition: opacity .18s` on the text,
     or the line is swapped while it is still half-visible and a phone
     composites the old line under the new one (Rue, 2026-09-09: "a weird
     layer on top of the page"). On any non-phone it stays the live 110ms.
     Both this file and its twin define PNQuotes the same way, since
     whichever parses first wins. */
  function fadeMs() {
    return document.documentElement.classList.contains('pn-phone') ? 200 : FADE;
  }

  function bind(box, el) {
    if (!box || !el) return null;
    var list;
    try { list = JSON.parse(box.getAttribute('data-quotes') || '[]'); }
    catch (e) { list = []; }
    list = (list || []).filter(function (q) { return q && String(q).trim(); });
    if (list.length < 2) return null;              /* nothing to rotate through */

    /* start from whichever line the server rendered, so the first change is
       a step forward rather than a jump back to the top */
    var here = list.indexOf((el.textContent || '').trim());
    var i = here < 0 ? 0 : here;
    var timer = null;

    function show(n) {
      i = ((n % list.length) + list.length) % list.length;
      el.style.opacity = '0';
      setTimeout(function () { el.textContent = list[i]; el.style.opacity = ''; }, fadeMs());
    }
    function run(ms) {
      clearInterval(timer);
      timer = setInterval(function () { show(i + 1); }, ms);
    }
    box.addEventListener('mouseenter', function () { show(i + 1); run(FAST); });
    box.addEventListener('mouseleave', function () { run(SLOW); });
    /* a background tab should not burn through the list */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) clearInterval(timer); else run(SLOW);
    });
    run(SLOW);
    return { show: show, count: list.length };
  }
  return { bind: bind };
})();

/* ============================================================
   Paranoise — Sticky Note product page behaviours.

   Deliberately does NOT touch the 3D deck. pn-turntable.js opens
   itself when a track plays and docks under `.pnb-console` on its
   own (homeScreenPoint / frameDeck / place). The handoff marks the
   opening camera BINDING, so nothing here re-frames it.
   ============================================================ */
(function () {
  'use strict';

  var root = document.querySelector('.pnsn');
  if (!root) return;

  var VIEW_W = 340;           /* carved-headline viewBox width */
  var STAGE_W = 1376, STAGE_H = 730;

  /* ---------- one-viewport frame ---------- */
  document.documentElement.classList.add('pnsn-lock');
  document.body.classList.add('pnsn-lock');

  var main  = root.querySelector('.pnsn-main');
  var stage = root.querySelector('.pnsn-stage');

  /* The theme header takes flow space above the stage; measure it so the
     stage fits the remaining viewport exactly (one screen, no page scroll). */
  function frame() {
    var hdr = document.querySelector('sticky-header, .header-wrapper, #shopify-section-header');
    var h = hdr ? hdr.getBoundingClientRect().height : 0;
    if (h > 0) root.style.setProperty('--pnsn-head', Math.round(h) + 'px');
  }

  /* ---------- two canvases ----------
     Same contract as the After Hours page (RESPONSIVE-RULES.md): below the
     breakpoint the page switches to a SECOND fixed-pixel composition rather
     than shrinking the wide one, which is what made narrow windows
     unreadable. WIDE keeps its one-screen fit (Rue's explicit rule for this
     page); COMPACT stacks the right-hand notes under the other two columns,
     scales by width only, and scrolls. */
  var BREAK = 1100, CMP_W = 900, CMP_MAX = 1.25;
  var curCompact = null, crow = null;
  var colL = root.querySelector('.pnsn-col-l');
  var colC = root.querySelector('.pnsn-col-c');
  var colR = root.querySelector('.pnsn-col-r');

  function relayout(compact) {
    if (!stage || !colL || !colC || !colR) return;
    if (compact) {
      if (!crow) { crow = document.createElement('div'); crow.className = 'pnsn-crow'; }
      stage.insertBefore(crow, colL);
      crow.appendChild(colL);
      crow.appendChild(colC);
      stage.appendChild(colR);            /* the notes column drops underneath */
    } else if (crow && crow.parentNode) {    /* only undo a swap that happened */
      stage.insertBefore(colL, crow);
      stage.insertBefore(colC, crow);
      stage.appendChild(colR);
      crow.parentNode.removeChild(crow);
    }
    root.setAttribute('data-sn-layout', compact ? 'compact' : 'wide');
    try { window.dispatchEvent(new Event('pn:relayout')); } catch (e) {}
  }

  function measure() {
    frame();
    if (!main || !stage) return;
    var w = main.clientWidth, h = main.clientHeight;
    if (!w) return;

    var compact = (window.innerWidth || w) < BREAK;
    if (compact !== curCompact) { curCompact = compact; relayout(compact); }

    if (compact) {
      /* the stacked canvas is as tall as its own content */
      stage.style.height = 'auto';
      var need = Math.max(stage.scrollHeight, 400);
      stage.style.height = '';
      root.style.setProperty('--pnsn-stageh', need + 'px');
      var sc = Math.min(w / CMP_W, CMP_MAX);
      if (!isFinite(sc) || sc <= 0) return;
      root.style.setProperty('--pnsn-scale', sc.toFixed(4));
      return;
    }
    if (!h) return;
    /* Nothing on this page scrolls, so the stage has to grow to whichever
       column is tallest (a 10-track LP, a long pulse note) and then be scaled
       down to the viewport as a whole.

       The stage is collapsed to 0 first: at its full height the columns are
       stretched flex children, so `margin-top:auto` on the quote and the
       deck zone's min-height would report back whatever height was just set
       and the measurement would ratchet upward instead of settling.
       Transforms never affect layout, so these are unscaled units. */
    root.style.setProperty('--pnsn-stageh', '0px');
    var need = STAGE_H;
    ['.pnsn-col-l', '.pnsn-col-c', '.pnsn-col-r'].forEach(function (sel) {
      var c = root.querySelector(sel);
      if (c && c.scrollHeight > need) need = c.scrollHeight;
    });
    root.style.setProperty('--pnsn-stageh', need + 'px');

    var s = Math.min((w - 24) / STAGE_W, (h - 16) / need);
    if (!isFinite(s) || s <= 0) return;
    root.style.setProperty('--pnsn-scale', s.toFixed(4));
  }
  measure();
  window.addEventListener('resize', measure);
  setTimeout(measure, 60);
  setTimeout(measure, 400);

  /* ============================================================
     Carved headline — wood-filled SVG text.
     Fit routine per the handoff: keep natural spacing when it fits;
     wrap to two lines, then step 41 → 34 → 28; only compress with
     textLength on a line that still overruns the viewBox.
     ============================================================ */
  (function carve() {
    var svg = root.querySelector('.pnsn-carve svg');
    if (!svg) return;
    var artist = svg.getAttribute('data-artist') || '';
    var album  = svg.getAttribute('data-album')  || '';
    var NS = 'http://www.w3.org/2000/svg';

    /* After Hours draws the artist in its own <svg> and the title in another,
       which is why its hover filter darkens the name alone. Safari only
       applies a CSS filter to an <svg> element -- not to a <g> inside one --
       so the same split is what makes the hover work here. The album <svg> is
       stacked over the artist one with an identical viewBox, so the wood
       pattern (userSpaceOnUse) lands on the same coordinates in both. */
    svg.classList.add('pnsn-artist-svg');
    var albumSvg = root.querySelector('.pnsn-album-svg');
    if (!albumSvg) {
      albumSvg = document.createElementNS(NS, 'svg');
      albumSvg.setAttribute('class', 'pnsn-album-svg');
      albumSvg.setAttribute('preserveAspectRatio', 'xMinYMid meet');
      albumSvg.setAttribute('aria-hidden', 'true');
      svg.parentNode.appendChild(albumSvg);
    }

    var probe = document.createElementNS(NS, 'text');
    probe.setAttribute('x', '-9999');
    probe.setAttribute('y', '-9999');
    probe.setAttribute('visibility', 'hidden');
    svg.appendChild(probe);

    function widthOf(str, weight, size) {
      probe.setAttribute('style',
        "font-family:'Bugrino',sans-serif;font-weight:" + weight +
        ';font-size:' + size + 'px;letter-spacing:' + (weight === 900 ? '-.02em' : '.01em'));
      probe.textContent = str;
      try { return probe.getComputedTextLength(); } catch (e) { return 0; }
    }

    /* split into two balanced lines, preferring a break before "&" */
    function split2(str) {
      var words = str.split(/\s+/).filter(Boolean);
      if (words.length < 2) return null;
      var amp = -1;
      for (var i = 1; i < words.length; i++) {
        if (words[i].charAt(0) === '&' || words[i].toLowerCase() === 'and') { amp = i; break; }
      }
      var at = amp;
      if (at < 1) {
        /* nearest space to the middle by character count */
        var half = str.length / 2, best = 1, bestD = Infinity, run = 0;
        for (var j = 0; j < words.length - 1; j++) {
          run += words[j].length + 1;
          var d = Math.abs(run - half);
          if (d < bestD) { bestD = d; best = j + 1; }
        }
        at = best;
      }
      return [words.slice(0, at).join(' '), words.slice(at).join(' ')];
    }

    function layout() {
      /* If this panel is hidden (the visitor chose After Hours, so the Sticky
         page has display:none) every text measurement reads 0 and the ladder
         would "fit" the whole name on one line at the largest size. Bail;
         the pn:mode hook below re-runs this the moment the panel shows. */
      if (!widthOf(artist || 'x', 900, 41)) return;

      /* A fine ladder from 54px down, rather than the old coarse 41/34/28.
         Every record then gets the LARGEST size that still fits the 340-wide
         viewBox, so a short name like "Trikk" sets much bigger while a long
         one like "Echonomist & Jenia Tarsol" settles where it always did
         instead of being compressed. */
      /* A long name goes onto TWO lines rather than shrinking to fit one.

         "Arke Sinth" set at 54 while "Timo van Luijk & Kris Vanderstraeten"
         had to drop to the mid-20s to fit a single line, so the headline size
         swung wildly from record to record. Wrapping lets a long name hold a
         size close to a short one.

         The two-line cap is not a taste decision, it is the band's height.
         .pnsn-ident is locked so the tracklist and the deck below it cannot
         move, and the carve is drawn at 132% of the 320 column, i.e. 1.242
         rendered units per viewBox unit. Measured, the band has 130 rendered
         units of room for the headline:

           viewBox height = 0.83fs + lines*0.976fs - 0.3fs + 7
           two lines: 2.482fs + 7 <= 130/1.242  ->  fs <= 39
           one line:  1.506fs + 7 <= 130/1.242  ->  fs <= 64

         So one line may go to 54 as before, two lines stop at 38. Change the
         band height and these two numbers have to be recomputed. */
      var MAX_TWO_LINE = 38;
      var sizes = [];
      for (var sz = 54; sz >= 20; sz -= 2) sizes.push(sz);
      var lines = null, fs = 20;
      for (var k = 0; k < sizes.length; k++) {
        var sz2 = sizes[k];
        if (widthOf(artist, 900, sz2) <= VIEW_W) { lines = [artist]; fs = sz2; break; }
        if (sz2 <= MAX_TWO_LINE) {
          var two = split2(artist);
          if (two && widthOf(two[0], 900, sz2) <= VIEW_W && widthOf(two[1], 900, sz2) <= VIEW_W) {
            lines = two; fs = sz2; break;
          }
        }
      }
      if (!lines) { lines = split2(artist) || [artist]; fs = 20; }

      /* The album title is a FIXED size, not a fraction of the artist size.
         Deriving it (fs * 0.56) meant a one-word artist that set at the top of
         the ladder (54) pushed its title to 30px, while a long artist name that
         wrapped to two lines capped at 38 and gave 21 - so the same field came
         out more than half again as big on one record as on another. 23 is the
         design's own number (the 41 -> 23 pair). Titles too long for the box
         still step down through the shrink loop below.

         The number is 14 because that is what "The Invisible Road (Original
         Recordings 1985-1990)" resolves to - the record Rue picked as the
         reference - and every other title had been rendering LARGER than it.
         Pinning the base there is the only value at which no record's title
         comes out bigger than that one, so the field is now identical on
         every product page. */
      var albumFs = 14;

      var step = Math.round(fs * 0.976);          /* 41 → 40, as in the design */
      var y0 = Math.round(fs * 0.83);             /* 41 → 34 */
      /* Fixed gap between the last artist line and the title baseline. This
         was `step - fs * 0.30`, i.e. derived from the artist size, so a 54px
         one-line artist opened a 37-unit gap while a 38px two-line artist
         opened 26 - the pair sat at a different distance on every record.
         26 is the gap on the reference record (Sussan Deyhim & Richard
         Horowitz), so that page is unchanged and every other page now
         matches it. */
      var ALBUM_GAP = 26;
      var albumY = y0 + (lines.length - 1) * step + ALBUM_GAP;

      /* clear previously drawn text, keep <defs> and the probe */
      Array.prototype.slice.call(
        svg.querySelectorAll('text.pnsn-line')
      ).concat(Array.prototype.slice.call(
        albumSvg.querySelectorAll('text.pnsn-line')
      )).forEach(function (n) {
        n.parentNode.removeChild(n);
      });

      lines.forEach(function (line, i) {
        var t = document.createElementNS(NS, 'text');
        t.setAttribute('class', 'pnsn-line pnsn-carve-artist');
        t.setAttribute('x', '0');
        t.setAttribute('y', String(y0 + i * step));
        t.setAttribute('fill', 'url(#pnwoodtxt)');
        t.setAttribute('filter', 'url(#pncarvetxt)');
        t.setAttribute('style', 'font-size:' + fs + 'px');
        t.textContent = line;
        if (widthOf(line, 900, fs) > VIEW_W) {
          t.setAttribute('textLength', String(VIEW_W - 2));
          t.setAttribute('lengthAdjust', 'spacingAndGlyphs');
        }
        svg.appendChild(t);
      });

      if (album) {
        var a = document.createElementNS(NS, 'text');
        a.setAttribute('class', 'pnsn-line pnsn-carve-album');
        a.setAttribute('x', '1');
        a.setAttribute('y', String(albumY));
        a.setAttribute('fill', 'url(#pnwoodtxt)');
        a.setAttribute('filter', 'url(#pncarvetxt)');
        a.setAttribute('style', 'font-size:' + albumFs + 'px');
        a.textContent = album;
        /* Set it smaller rather than crushing it. lengthAdjust
           "spacingAndGlyphs" does not scale type — it squeezes each glyph
           horizontally and leaves the height alone, so a long title like
           "The Invisible Road (Original Recordings 1985-1990)" came out
           visibly narrow, as if the font had been condensed. Step the size
           down first; only squeeze if it still will not fit at the floor. */
        var afs = albumFs;
        while (afs > 11 && widthOf(album, 300, afs) > VIEW_W) afs -= 1;
        if (afs !== albumFs) a.setAttribute('style', 'font-size:' + afs + 'px');
        if (widthOf(album, 300, afs) > VIEW_W) {
          a.setAttribute('textLength', String(VIEW_W - 2));
          a.setAttribute('lengthAdjust', 'spacingAndGlyphs');
        }
        albumSvg.appendChild(a);
      }

      /* trailing room under the title: 22 left a visible gap above the
         label row now that the label sits directly beneath it */
      var vb = '0 0 ' + VIEW_W + ' ' + (albumY + 7);
      svg.setAttribute('viewBox', vb);
      albumSvg.setAttribute('viewBox', vb);
    }

    layout();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { layout(); measure(); });
    }
    /* A canvas swap changes nothing about this box's width, but Chrome can
       hand back stale text metrics right after one, and the ladder then
       picks a size whose lines overrun the viewBox. Re-run on the swap. */
    window.addEventListener('pn:relayout', function () {
      layout(); measure();
      requestAnimationFrame(function () { layout(); measure(); });
      setTimeout(function () { layout(); measure(); }, 120);
    });
    window.addEventListener('pn:mode', function () {
      setTimeout(function () { layout(); measure(); }, 30);
    });
  })();

  /* ============================================================
     Centre stage — sleeve flip + mouse-move tilt
     ============================================================ */
  (function sleeve() {
    var tilt = root.querySelector('.pnsn-tilt');
    var flip = root.querySelector('.pnsn-flip');
    var col  = root.querySelector('.pnsn-col-c');
    var hint = root.querySelector('.pnsn-stagecap-hint');
    if (!flip || !tilt || !col) return;

    flip.addEventListener('click', function () {
      root.classList.toggle('is-flipped');
      if (hint) {
        hint.textContent = root.classList.contains('is-flipped')
          ? 'Click sleeve — front cover' : 'Click sleeve — back cover';
      }
    });

    var over = false;
    col.addEventListener('mouseenter', function () { over = true; });
    col.addEventListener('mouseleave', function () {
      over = false;
      tilt.style.transition = 'transform .5s ease';
      tilt.style.transform = 'perspective(1100px)';
      setTimeout(function () { tilt.style.transition = ''; }, 520);
    });
    window.addEventListener('mousemove', function (e) {
      if (!over) return;
      var r = tilt.getBoundingClientRect();
      if (!r.width || !r.height) return;
      var dx = (e.clientX - r.left - r.width / 2) / r.width;
      var dy = (e.clientY - r.top - r.height / 2) / r.height;
      var cl = function (v) { return Math.max(-0.8, Math.min(0.8, v)); };
      tilt.style.transform =
        'perspective(1100px) rotateY(' + (cl(dx) * 10) + 'deg) rotateX(' + (cl(-dy) * 8) + 'deg)';
    });
  })();

  /* ============================================================
     Tracklist rows.
     paranoise-player.js renders the rows and record-data-block.liquid
     appends the bpm·key readout; neither emits a duration. The
     durations exist in the Discogs description that the player is
     already given, so they are read from there rather than invented.
     ============================================================ */
  (function tracks() {
    var mount = document.getElementById('pn-tracklist-mount');
    if (!mount) return;

    var durations = {};
    try {
      var raw = document.getElementById('pn-product-data');
      var desc = raw ? (JSON.parse(raw.textContent).description || '') : '';
      desc.split('|||').forEach(function (seg) {
        var m = seg.replace(/\s+/g, ' ').trim()
          .match(/^([A-Za-z]{1,3}\d{0,2})\.\s+.*?(\d{1,2}:\d{2})\s*$/);
        if (m) durations[m[1].toUpperCase()] = m[2];
      });
    } catch (e) { /* no durations — rows simply render without one */ }

    /* "04:23" and "4:23" both occur in the source descriptions. */
    function fmtDur(v) {
      var t = (v || '').trim();
      var m = t.match(/^(\d{1,2}):(\d{2})$/);
      return m ? (parseInt(m[1], 10) + ':' + m[2]) : t;
    }

    function decorate() {
      var items = mount.querySelectorAll('.pn-tracklist-item');
      if (!items.length) return false;
      /* SIDE A / SIDE B / SIDE C… derived from the side labels themselves.

         The headers used to come only from record-data-block.liquid, which
         emits them for records whose enriched tracklist metafield was written
         — a minority. Every record fed from the collector now carries proper
         A1/B2/C3 labels, so the header is derivable: insert one whenever the
         leading letter changes. Skipped where a header is already in place,
         so record-data-block still wins where it did the work. */
      var prevLetter = null;
      Array.prototype.slice.call(items).forEach(function (item) {
        var se = item.querySelector('.pn-tracklist-side');
        var p = se ? se.textContent.trim().toUpperCase() : '';
        var letter = /^([A-Z])\s*\d/.test(p) ? p.charAt(0) : null;
        if (!letter || letter === prevLetter) return;
        prevLetter = letter;
        var before = item.previousElementSibling;
        if (before && before.classList && before.classList.contains('pn-sheet-side')) return;
        var hd = document.createElement('div');
        hd.className = 'pn-sheet-side';
        hd.textContent = 'Side ' + letter;
        item.parentNode.insertBefore(hd, item);
      });

      Array.prototype.slice.call(items).forEach(function (item) {
        /* "120 BPM · 8B" → "120 · 8B", per the design */
        var ro = item.querySelector('.pn-sheet-readout');
        if (ro && ro.textContent.indexOf(' BPM') !== -1) {
          ro.textContent = ro.textContent.replace(/\s*BPM/g, '');
        }
        /* The duration cell is added to EVERY row, empty when that track has
           no time. It used to be skipped entirely, and because the row is a
           flex line the columns after it slid left by its width — so on a
           record where only some tracks carry a duration, the readouts did
           not line up down the list. Both panels share this one mount, so
           the ragged rows showed up in After Hours too. A reserved slot keeps
           the column straight whether the value is there or not. */
        if (item.querySelector('.pnsn-dur')) return;
        var sideEl = item.querySelector('.pn-tracklist-side');
        var pos = sideEl ? sideEl.textContent.trim().toUpperCase() : '';
        var d = document.createElement('span');
        d.className = 'pnsn-dur';
        /* The duration was being shown TWICE: the player renders its own
           .pn-tracklist-duration next to the play button and this cell repeats
           it on the right, in a different format ("4:23" against "04:23").
           That cell is now hidden in both panels, so this is the only duration
           on the row — which means it has to carry the best value available.
           The two parsers read the same Discogs description but disagree on
           some records (on Sussan Deyhim the player finds nothing while this
           parse finds two times; on Filmmaker both find all of them), so take
           whichever is populated. Source formatting is inconsistent between
           records, hence the pad-stripping: one column, one format. */
        var own = item.querySelector('.pn-tracklist-duration');
        d.textContent = fmtDur(durations[pos] || (own ? own.textContent : ''));
        item.appendChild(d);
      });

      /* A duration column with holes in it reads as broken, and roughly half
         the catalogue has no times at all (823 of 1,654 tracks in a
         250-record sample; Discogs simply never listed them). Rue's rule: the
         column appears only when the record is essentially complete — AT MOST
         ONE track may be missing a duration. Two or more blanks and the whole
         column is dropped for that record, so what shows is either a full
         column or none.

         The flag goes on <html>, not on the mount, because After Hours drifts
         a deep CLONE of the mount for its seamless loop; the clone has no id
         and would keep its cells if the rule were scoped to #pn-tracklist-mount.
         Recomputed on every decorate(), so a mode switch re-evaluates it. */
      var blanks = 0;
      Array.prototype.slice.call(items).forEach(function (item) {
        var c = item.querySelector('.pnsn-dur');
        if (c && !c.textContent.trim()) blanks++;
      });
      document.documentElement.setAttribute('data-pn-dur', blanks > 1 ? 'off' : 'on');
      return true;
    }

    if (!decorate()) {
      var obs = new MutationObserver(function () { decorate(); });
      obs.observe(mount, { childList: true, subtree: true });
      setTimeout(function () { obs.disconnect(); decorate(); }, 15000);
    }

    /* Mirror the player's real play state onto the page (equalizer,
       now-playing bar). The deck opens and plays itself; nothing here
       drives it.

       NOT `.pn-track-trigger.is-playing`: paranoise-player.js sets that from
       its own retired audio path, and on this theme playback belongs to the
       3D deck, so the class never appears. The deck's own live region is the
       signal that actually tracks it. */
    var label = root.querySelector('.pnsn-nowbar-track');
    var lastTrigger = null;
    mount.addEventListener('click', function (e) {
      var b = e.target && e.target.closest && e.target.closest('.pn-track-trigger');
      if (b) lastTrigger = b;
    });

    function isPlaying() {
      var live = document.querySelector('.pn-tt__live');
      if (live) return /playing/i.test(live.textContent || '');
      return !!mount.querySelector('.pn-track-trigger.is-playing');
    }

    function sync() {
      var on = isPlaying();
      root.classList.toggle('is-playing', on);
      if (!on || !label) return;
      var txt = '';
      if (lastTrigger) {
        txt = (lastTrigger.dataset.side ? lastTrigger.dataset.side + '. ' : '') +
              (lastTrigger.dataset.title || '');
      }
      if (!txt) {
        var t1 = document.querySelector('.pn-tt__t1');
        txt = t1 ? t1.textContent.trim() : '';
      }
      if (txt) label.textContent = txt;
    }
    setInterval(sync, 500);
  })();

  /* ============================================================
     Buyer quote — shuffles on hover
     ============================================================ */
  (function quotes() {
    var box = root.querySelector('.pnsn-quote');
    var el  = root.querySelector('.pnsn-quote-text');
    if (!box || !el) return;
    /* was: advance only when the mouse left. Now it walks on its own and
       runs fast while hovered - see PNQuotes at the top of this file. */
    window.PNQuotes.bind(box, el);
  })();

  /* Artist hover lives in CSS now (.pnsn-carve:hover .pnsn-artist-svg),
     exactly as in After Hours - the split above is what let it move there. */

  /* ============================================================
     Inline 3D deck. The player builds itself into #pn-deck-mount and sizes
     to that container; nothing here touches its camera.
     ============================================================ */
  (function deck() {
    var mount = document.getElementById('pn-deck-mount');
    if (!mount) return;

    /* pn-turntable-bridge.js only imports the player on the first play click,
       so on an inline page nothing exists to mount yet. Pull the module in
       ourselves; it is the same URL the bridge uses, so the module cache
       hands the bridge this very instance when a track is later played. */
    function start(TT) {
      if (!TT || !TT.mount) return;
      TT.mount();
      watch(TT);
    }

    var A = window.PN_TT_ASSETS || {};
    try {
      /* built via Function so a browser without dynamic import throws here
         rather than failing to parse this whole file */
      var dynamicImport = new Function('u', 'return import(u)');
      dynamicImport(A.player || 'pn-turntable.js')
        .then(function (m) { start((m && m.PNTurntable) || window.PNTurntable); })
        .catch(function () { poll(); });
    } catch (e) { poll(); }

    /* fallback: the bridge will have defined it once anything plays */
    function poll() {
      var tries = 0;
      (function wait() {
        if (window.PNTurntable && window.PNTurntable.mount) { start(window.PNTurntable); return; }
        if (++tries > 150) return;
        setTimeout(wait, 100);
      })();
    }

    /* Re-centre after the stage rescales. The player already re-renders on a
       window resize; this covers layout changes that are not window resizes. */
    function watch(TT) {
      if (typeof ResizeObserver !== 'function' || !TT.resetView) return;
      var lastW = 0, lastH = 0, t = null;
      new ResizeObserver(function () {
        var r = mount.getBoundingClientRect();
        if (Math.abs(r.width - lastW) < 2 && Math.abs(r.height - lastH) < 2) return;
        lastW = r.width; lastH = r.height;
        clearTimeout(t);
        t = setTimeout(function () { try { TT.resetView(); } catch (e) {} }, 120);
      }).observe(mount);
    }
  })();

  /* ============================================================
     Add to cart — AJAX, "ADDED ✓" for ~1.6s
     ============================================================ */
  (function cart() {
    var form = root.querySelector('.pnsn-form');
    var btn  = root.querySelector('.pnsn-add');
    if (!form || !btn) return;
    var span = btn.querySelector('span');
    var busy = false;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (busy || btn.disabled) return;
      busy = true;
      var original = span ? span.textContent : '';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: new FormData(form)
      })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
        .then(function () {
          if (span) span.textContent = 'Added ✓';
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        })
        .catch(function () { if (span) span.textContent = 'Try again'; })
        .then(function () {
          setTimeout(function () {
            if (span) span.textContent = original;
            busy = false;
          }, 1600);
        });
    });
  })();
})();

/* ============================================================
   SIMILAR RECORDS — the record-sheet note flips over.
   Rue's brief: kill the "You may also like" carousel at the bottom of the
   page and surface similar records inside the design itself, the way the
   After Hours mascot does. Here the record-sheet note is the door: click it
   and the paper turns over to a list of related records.
   Data comes from the same place as After Hours - sections/pn-similar.liquid
   rendered through the Section Rendering API, so it can read metafields the
   JSON recommendations endpoint cannot.
   ============================================================ */
(function () {
  'use strict';
  var root = document.querySelector('.pnsn');
  if (!root) return;
  var note = root.querySelector('.pnsn-note--sheet');
  if (!note) return;

  /* ---------- build the two faces around the note's own content ---------- */
  var front = document.createElement('div'); front.className = 'pnsn-nface pnsn-nface--front';
  var back  = document.createElement('div'); back.className  = 'pnsn-nface pnsn-nface--back';
  var inner = document.createElement('div'); inner.className = 'pnsn-nflip';
  while (note.firstChild) front.appendChild(note.firstChild);
  inner.appendChild(front); inner.appendChild(back);
  note.appendChild(inner);
  note.classList.add('pnsn-flip-host');

  /* The note's header carries both faces: the one you are looking at on the
     left, the one you can turn to on the right. Put SIMILAR in the existing
     RECORD SHEET head row so the two sit on the same line; fall back to the
     face itself if that row is ever absent. */
  var hint = document.createElement('button');
  hint.type = 'button'; hint.className = 'pnsn-simhint';
  hint.setAttribute('aria-expanded', 'false');
  hint.textContent = 'Similar';
  (front.querySelector('.pnsn-sheet-head') || front).appendChild(hint);

  back.innerHTML =
    '<div class="pnsn-simhead"><span class="pnsn-simttl">Similar</span>' +
    '<button type="button" class="pnsn-simback">Record sheet</button></div>' +
    '<div class="pnsn-simwin"><div class="pnsn-simtrack"></div></div>';
  var win = back.querySelector('.pnsn-simwin');
  var track = back.querySelector('.pnsn-simtrack');

  /* ---------- data ---------- */
  var loaded = false, recs = [];
  function load() {
    if (loaded) return Promise.resolve();
    return window.PNSimilar.load(10)
      .then(function (list) { recs = list || []; loaded = true; })
      .catch(function (e) { console.warn('[pn-sticky] similar records unavailable', e); });
  }

  function row(rec) {
    var a = document.createElement('a');
    a.className = 'pnsn-simrow'; a.href = rec.href || '#';
    var img = rec.img
      ? '<img class="pnsn-simcover" src="' + rec.img + '" alt="" loading="lazy" width="34" height="34">'
      : '<span class="pnsn-simcover pnsn-simcover--none"></span>';
    a.innerHTML = img +
      '<span class="pnsn-simtxt"><span class="pnsn-simartist"></span>' +
      '<span class="pnsn-simname"></span><span class="pnsn-siminfo"></span></span>' +
      '<span class="pnsn-simprice"></span>';
    a.querySelector('.pnsn-simartist').textContent = rec.artist || rec.name || '';
    a.querySelector('.pnsn-simname').textContent = rec.title || '';
    var info = [];
    if (rec.label) info.push(rec.label);
    if (rec.bpm) info.push(rec.bpm + ' BPM');
    a.querySelector('.pnsn-siminfo').textContent = info.join(' · ');
    a.querySelector('.pnsn-simprice').textContent = rec.price || '';
    return a;
  }

  function render() {
    track.textContent = '';
    if (!recs.length) {
      var p = document.createElement('p');
      p.className = 'pnsn-simempty'; p.textContent = 'Nothing similar yet.';
      track.appendChild(p); return;
    }
    recs.forEach(function (r) { track.appendChild(row(r)); });
  }

  /* Rue: no motion on this face - the list just sits there. It scrolls if the
     records overrun the paper, with the scrollbar chrome hidden as everywhere
     else on this page. */

  /* ---------- flip ---------- */
  var open = false;
  function setOpen(v) {
    open = v;
    note.classList.toggle('is-flipped', v);
    hint.setAttribute('aria-expanded', v ? 'true' : 'false');
    if (v) load().then(function () { if (open) { render(); win.scrollTop = 0; } });
  }
  hint.addEventListener('click', function (e) { e.stopPropagation(); setOpen(!open); });
  back.querySelector('.pnsn-simback').addEventListener('click', function (e) { e.stopPropagation(); setOpen(false); });
  /* clicking the paper itself opens it too, but never a link inside the list */
  note.addEventListener('click', function (e) {
    if (e.target.closest('a, button')) return;
    setOpen(!open);
  });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && open) setOpen(false); });
  /* the list only animates while this mode is the one on screen */
  /* leaving the mode closes the note so it never comes back mid-flip */
  window.addEventListener('pn:mode', function () {
    var on = (document.documentElement.getAttribute('data-pn-mode') || 'sticky') === 'sticky';
    if (!on && open) setOpen(false);
  });
})();

/* ============================================================
   SLOGAN WINDMILL — the After Hours wheel, re-hung for this page.

   There it sits on the right edge with four fifths of it off-screen. Here
   Rue wants it centred under the menu, so the WHOLE wheel is on the page and
   the geometry changes with it:

   · The hub is 1270 stage units above the stage on a 1400 ring, so the rim's
     lowest point is y=130 — the same shallow strip After Hours shows, clear
     of the record image whose top measured at y=166.
   · ONE blade per slogan. Turning the text 90 degrees (see .pnsn-clock-txt)
     makes it tangential, so a slogan's length now eats the gap between
     neighbouring blades; twins, or the smaller 780 ring, piled the words on
     top of each other. 35 blades on a 1400 ring leave 251 units of arc.
   · Period scales with the radius, so the slogans drift past at the same
     speed as they do on the other page rather than racing.
   ============================================================ */
(function () {
  var root = document.querySelector('.pnsn');
  if (!root) return;
  var host = root.querySelector('.pnsn-stage');    /* scales with the design */
  var pane = root.querySelector('.pnsn-main');     /* where the pointer is tracked */
  if (!host || !pane) return;

  var SLOGANS = [
    'Dig. Then Spin.', 'Dig What You Spin.', 'Spin What You Dig.',
    'Dig It. Spin It.', 'You Dig It. You Spin It.', 'Dig Your Sound.',
    'Find Your Spin.', 'Dig Deep. Spin Free.', 'Dig Before You Spin.',
    'What You Dig Is What You Spin.', 'Dig Deeper.', 'Always Digging.',
    'Born to Dig.', 'Dig Responsibly.', 'Dig With Purpose.', 'Dig Beyond.',
    'Dig the Noise.', 'Dig Strange.', 'Dig First.', 'Found While Digging.',
    'Do You Even Dig?', 'Dig Happens.', 'Still Digging?', 'Never Stop Digging.',
    'Dig Now. Think Later.', 'One More Crate.', 'Just One More Record.',
    'You Had Me at Vinyl.', 'Digging Is the Problem.', 'Vinyl Is the Solution.',
    'Keep Digging Until Closing Time.', 'No Digging, No Glory.',
    'Keep Digging.', 'Do You Dig?', 'Deep Diggers.'
  ];

  var R = 1400;                /* overridden from CSS once the wheel is in the DOM */
  var n = SLOGANS.length;
  var period = 270;            /* 150s at After Hours' r=780, scaled to r=1400 */

  function active() {
    return (document.documentElement.getAttribute('data-pn-mode') || 'sticky') === 'sticky';
  }

  var clock = document.createElement('div');
  clock.className = 'pnsn-clock';
  var face = document.createElement('div');
  face.className = 'pnsn-clock-face';
  var ring = document.createElement('div');
  ring.className = 'pnsn-clock-ring';

  for (var i = 0; i < n; i++) {
    var blade = document.createElement('span');
    blade.className = 'pnsn-clock-hand';
    /* max four words to a line; the rest drops to a second row */
    var text = SLOGANS[i % SLOGANS.length];
    var words = text.split(/\s+/);
    var rows = words.length > 4 ? [words.slice(0, 4).join(' '), words.slice(4).join(' ')]
                                : [text];
    var txt = document.createElement('span');
    txt.className = 'pnsn-clock-txt';
    rows.forEach(function (r) {
      var line = document.createElement('span');
      line.className = 'pnsn-clock-line';
      line.textContent = r;
      txt.appendChild(line);
    });
    blade.appendChild(txt);
    blade.style.transform = 'rotate(' + ((360 / n) * i) + 'deg)';
    ring.appendChild(blade);
  }
  face.appendChild(ring);
  clock.appendChild(face);
  /* first child, so the notes, sleeve and deck all paint over it */
  host.insertBefore(clock, host.firstChild);

  var declared = parseFloat(getComputedStyle(clock).getPropertyValue('--pnsn-clock-r'));
  if (declared > 0) R = declared;

  var angle = 0, hover = false, raf = null, last = 0;
  function step(ts) {
    if (!last) last = ts;
    var dt = Math.min((ts - last) / 1000, 0.1);
    last = ts;
    if (!hover) angle -= (360 / period) * dt;       /* anticlockwise */
    ring.style.transform = 'rotate(' + angle.toFixed(3) + 'deg)';
    raf = requestAnimationFrame(step);
  }
  function run() { if (!raf) { last = 0; raf = requestAnimationFrame(step); } }
  function stop() { if (raf) { cancelAnimationFrame(raf); raf = null; } }

  /* The wheel must not swallow clicks on the sleeve or the notes underneath
     it, so the face stays pointer-transparent and the pointer is tested
     against the ring's own geometry instead. */
  function overWheel(x, y) {
    var f = face.getBoundingClientRect();
    if (!f.width) return false;
    var cx = f.left + f.width / 2, cy = f.top + f.height / 2;
    var dx = x - cx, dy = y - cy;
    var d = Math.sqrt(dx * dx + dy * dy);
    var unit = f.width / (2 * R);                   /* device px per stage unit */
    return d <= (R + 10) * unit && d >= (R - 200) * unit;   /* only 130 of the rim is on the page */
  }
  pane.addEventListener('mousemove', function (e) { hover = overWheel(e.clientX, e.clientY); });
  pane.addEventListener('mouseleave', function () { hover = false; });
  pane.addEventListener('wheel', function (e) {
    if (!active() || !overWheel(e.clientX, e.clientY)) return;
    e.preventDefault();                             /* scrub instead of scrolling */
    angle -= e.deltaY * 0.06;
    ring.style.transform = 'rotate(' + angle.toFixed(3) + 'deg)';
  }, { passive: false });

  if (active()) run();
  window.addEventListener('pn:mode', function () { if (active()) run(); else stop(); });
})();

/* ============================================================
   window.PNSimilar — where "similar records" comes from.

   NOT Shopify's recommendation engine. `/recommendations/products` with
   intent=related ranks on order history and product type, which on a
   one-copy vinyl catalogue put a hard rock record next to a techno 12".
   This asks the store's OWN taxonomy instead: the page already renders
   filter links built from each record's style/genre metafields, e.g.
       /collections/all?filter.p.m.custom.style=Tech+House
   so we request sections/pn-similar.liquid on those URLs — every candidate
   already shares the tag — then rank by how many style tags overlap.

   Defined by whichever of pn-sticky.js / pn-afterhours.js runs first.
   ============================================================ */
if (!window.PNSimilar) window.PNSimilar = (function () {
  'use strict';
  var cache = null, pending = null;

  /* ============================================================
     ELIGIBILITY GATE (handoff §B3)

     Rue's rule: only records with similar tags, and from the same label.
     Read literally that is an AND, and the catalogue cannot carry it — 921
     records are the only record she owns on their label, and the median label
     holds exactly one, so a strict AND leaves 46% of product pages with an
     empty strip. Measured before writing this:

       gate                    median candidates   pages with NOTHING
       same tag OR same label        289                  92
       same label AND same tag         1               1,414   (46%)

     So the gate ships as OR, and "both" is expressed as the TOP TIER instead
     of as a filter. Rue chose this. PN_SIMILAR_STRICT flips it to the literal
     reading (tier 1 only) without touching anything else.

     Everything else — BPM closeness, stock, same artist, recency — is a
     TIE-BREAKER INSIDE the eligible set, never a reason to appear. That is
     what the old scorer got wrong: it was purely additive with no gate, so a
     record with nothing in common still scored 38 on BPM proximity plus a
     stock bonus and got shown.
     ============================================================ */

  var TIERS = 4;   /* 1 label+tag · 2 label · 3 two-plus tags · 4 one tag */

  /* §B3: a placeholder is not a shared label. Two unrelated records both
     marked "Not On Label" have nothing to do with each other. */
  var PLACEHOLDER = /^(not on label|unknown|various|various artists|white label|no label|self[\s-]?released|n\/a|-)$/i;
  function realLabel(v) {
    var t = String(v || '').trim();
    return (!t || PLACEHOLDER.test(t)) ? '' : t.toLowerCase();
  }

  function paramValue(href, key) {
    var m = String(href).match(new RegExp('[?&]' + key + '=([^&#]*)'));
    return m ? decodeURIComponent(m[1].replace(/\+/g, ' ')) : '';
  }
  function uniq(a) { return a.filter(function (v, i) { return a.indexOf(v) === i; }); }
  function asList(v) {
    if (!v) return [];
    if (Array.isArray(v)) return v.map(String);
    return String(v).split(/\s*,\s*/).filter(Boolean);
  }
  function norm(v) { return String(v || '').trim().toLowerCase(); }
  function currentHandle() {
    var seg = location.pathname.split('/').filter(Boolean);
    var i = seg.indexOf('products');
    return i >= 0 && seg[i + 1] ? seg[i + 1] : '';
  }

  /* the record's own tags and label, read off the filter links the page
     already renders — no extra request, and no second source of truth */
  function myTags() {
    var styleUrls = [], genreUrl = '';
    Array.prototype.forEach.call(
      document.querySelectorAll('a[href*="filter.p.m.custom.style="]'),
      function (a) { var h = a.getAttribute('href'); if (h && styleUrls.indexOf(h) < 0) styleUrls.push(h); });
    var g = document.querySelector('a[href*="filter.p.m.custom.genre="]');
    if (g) genreUrl = g.getAttribute('href') || '';
    var lv = document.querySelector('a[href*="filter.p.vendor="]');
    return {
      styleUrls: styleUrls,
      genreUrl: genreUrl,
      labelUrl: lv ? (lv.getAttribute('href') || '') : '',
      label: lv ? realLabel(lv.textContent) : '',
      styles: uniq(styleUrls.map(function (u) { return paramValue(u, 'filter\\.p\\.m\\.custom\\.style'); }).filter(Boolean)),
      genre: genreUrl ? paramValue(genreUrl, 'filter\\.p\\.m\\.custom\\.genre') : ''
    };
  }

  /* this record's own tempo, for the BPM tie-breaker: the median of its
     tracks in PN_TRACK_META. No separate BPM field is rendered for the
     record as a whole, and the per-track table is already on the page. */
  function myBpm() {
    try {
      var t = window.PN_TRACK_META; if (!t) return null;
      var b = new TextEncoder().encode(currentHandle()), h = 0x811c9dc5;
      for (var i = 0; i < b.length; i++) { h ^= b[i]; h = Math.imul(h, 0x01000193) >>> 0; }
      var blob = t[h.toString(36)]; if (!blob) return null;
      var v = blob.split(';').map(function (r) { return parseFloat(r.split('|')[1]); })
                  .filter(function (n) { return isFinite(n) && n !== 200; })
                  .sort(function (a, c) { return a - c; });
      return v.length ? v[Math.floor(v.length / 2)] : null;
    } catch (e) { return null; }
  }

  function fetchSection(url, src) {
    var u = url + (url.indexOf('?') < 0 ? '?' : '&') + 'section_id=pn-similar';
    return fetch(u, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
      .then(function (html) {
        var d = document.createElement('div'); d.innerHTML = html;
        var tag = d.querySelector('script[data-pn-similar]');
        var list = tag ? JSON.parse(tag.textContent || '[]') : [];
        list.forEach(function (rec) { if (rec) rec._src = src; });
        return list;
      })
      .catch(function (e) { console.warn('[pn-similar] ' + url + ' failed', e); return []; });
  }

  function load(want) {
    want = want || 10;
    if (cache) return Promise.resolve(cache);
    if (pending) return pending;

    var tags = myTags(), mine = currentHandle(), myLbl = tags.label, myTempo = myBpm();
    var myArtist = norm((window.PN_PRODUCT_TRUTH || {}).artist);

    /* Two sources, matching the two halves of the gate. The label query is
       what lets a same-label record with no shared tag qualify at all — the
       tag queries alone could never surface one. */
    var jobs = tags.styleUrls.slice(0, 3).map(function (u) { return { url: u, src: 'tag' }; });
    if (tags.genreUrl) jobs.push({ url: tags.genreUrl, src: 'tag' });
    if (tags.labelUrl) jobs.push({ url: tags.labelUrl, src: 'label' });

    /* §B6: if the record has neither tags nor a label there is nothing
       honest to show. Show nothing. Do NOT fall back to Shopify's engine. */
    if (!jobs.length) { cache = []; return Promise.resolve(cache); }

    var seen = {}, out = [];
    pending = Promise.all(jobs.map(function (j) { return fetchSection(j.url, j.src); }))
      .then(function (lists) {
        lists.forEach(function (list) {
          list.forEach(function (rec) {
            if (!rec || !rec.handle || rec.handle === mine) return;
            if (seen[rec.handle]) {
              /* reached by both queries: keep the label provenance */
              if (rec._src === 'label') seen[rec.handle]._src = 'label';
              return;
            }
            var st = asList(rec.style), gn = asList(rec.genre);
            var shared = st.filter(function (x) { return tags.styles.indexOf(x) >= 0; }).length;
            if (tags.genre && gn.indexOf(tags.genre) >= 0) shared += 1;

            var sameLabel = rec._src === 'label' ||
                            (myLbl && realLabel(rec.label) === myLbl);

            /* THE GATE. Nothing else gets in. */
            if (!shared && !sameLabel) return;

            rec._shared = shared;
            rec._sameLabel = !!sameLabel;
            rec._tier = sameLabel ? (shared ? 1 : 2) : (shared >= 2 ? 3 : 4);
            rec._sameArtist = myArtist && norm(rec.artist) === myArtist ? 1 : 0;
            var b = parseFloat(String(rec.bpm || '').split(/[^\d.]+/).filter(Boolean)[0]);
            rec._bpmGap = (myTempo && isFinite(b)) ? Math.abs(b - myTempo) : 999;
            rec._stock = rec.available ? 1 : 0;
            rec._new = parseInt(rec.created, 10) || 0;
            seen[rec.handle] = rec;
            out.push(rec);
          });
        });

        if (window.PN_SIMILAR_STRICT === true) {
          out = out.filter(function (r) { return r._tier === 1; });
        }

        /* tier first, then the tie-breakers in the order §B3 sets out */
        out.sort(function (a, b) {
          return (a._tier - b._tier)
              || (b._shared - a._shared)
              || (a._bpmGap - b._bpmGap)
              || (b._sameArtist - a._sameArtist)
              || (b._stock - a._stock)
              || (b._new - a._new);
        });

        /* §B6/B7: never padded to a count. Sold-out records stay — a sold-out
           record on the same label beats a random in-stock one and shows the
           depth of the catalogue — they just rank below in-stock ones. */
        cache = out.slice(0, want);
        pending = null;
        return cache;
      });
    return pending;
  }
  return { load: load, tags: myTags };
})();
